import { logger } from '@/core/logging/logger'

/**
 * Thin fetch wrapper for the Worker API.
 *
 * The base URL comes from VITE_API_BASE. If it's empty we fall back to
 * same-origin `/api`, which is what you get if you proxy through Pages instead
 * of calling the workers.dev URL directly — see 07-sync-protocol.md §5.1.
 */

/**
 * Read at call time, not at module load.
 *
 * Module-level capture makes the value untestable -- `vi.stubEnv` cannot reach
 * a const that was already frozen at import. It also means a test's result
 * depends on whether the developer happens to have a .env file, which is how
 * this went wrong the first time.
 */
export function getApiBase(): string {
  const raw = import.meta.env['VITE_API_BASE'] as string | undefined
  return (raw ?? '').trim().replace(/\/+$/, '')
}

/** Same-origin when no base is configured, e.g. behind a Pages /api proxy. */
export function apiUrl(path: string): string {
  const p = path.startsWith('/') ? path : `/${path}`
  return `${getApiBase()}${p}`
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

export interface ApiOptions {
  token?: string | undefined
  signal?: AbortSignal | undefined
}

export async function apiGet<T>(path: string, opts: ApiOptions = {}): Promise<T> {
  return request<T>('GET', path, undefined, opts)
}

export async function apiPost<T>(
  path: string,
  body: unknown,
  opts: ApiOptions = {},
): Promise<T> {
  return request<T>('POST', path, body, opts)
}

async function request<T>(
  method: 'GET' | 'POST',
  path: string,
  body: unknown,
  opts: ApiOptions,
): Promise<T> {
  const headers: Record<string, string> = {}
  if (body !== undefined) headers['Content-Type'] = 'application/json'
  if (opts.token) headers['Authorization'] = `Bearer ${opts.token}`

  const init: RequestInit = { method, headers }
  if (body !== undefined) init.body = JSON.stringify(body)
  if (opts.signal) init.signal = opts.signal

  const res = await fetch(apiUrl(path), init)

  if (!res.ok) {
    const text = await res.text().catch(() => '')
    logger.warn('sync', `${method} ${path} -> ${res.status}`, text)
    throw new ApiError(res.status, text || res.statusText)
  }

  return (await res.json()) as T
}

export interface HealthResponse {
  ok: boolean
  service: string
  time: number
  database: 'connected' | 'unavailable' | 'not_configured'
}

export function checkHealth(signal?: AbortSignal): Promise<HealthResponse> {
  return apiGet<HealthResponse>('/api/health', { signal })
}
